import { useMemo, useState } from "react";
import {
  Archive,
  ArchiveRestore,
  ChevronDown,
  ChevronUp,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import {
  ENTITY_TYPES,
  FIELD_TYPES,
  type EntityType,
  type FieldType,
} from "#shared/constants.ts";
import type { FieldConfig, FieldDef, SelectOption } from "#shared/schemas/fieldDef.ts";
import { ConfirmDialog } from "@web/components/ConfirmDialog.tsx";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { Button } from "@web/components/ui/button.tsx";
import { Checkbox } from "@web/components/ui/checkbox.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { nextDefaultName, slugify } from "@web/lib/names.ts";
import { api } from "@web/lib/api.ts";
import { cn } from "@web/lib/utils.ts";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAllFieldDefs,
  useArchiveField,
  useCreateField,
  useDeleteField,
  useReorderField,
  useUpdateField,
} from "./fieldsApi.ts";

const INPUT =
  "h-8 w-full min-w-0 rounded-md border border-line bg-panel px-2 text-sm text-text placeholder:text-faint focus-visible:outline-none disabled:opacity-50";

const TYPE_LABEL: Record<FieldType, string> = {
  text: "Text",
  longtext: "Long text",
  number: "Number",
  date: "Date",
  url: "URL",
  select: "Select",
  multiselect: "Multi-select",
  checkbox: "Checkbox",
  country: "Country",
};

const ENTITY_LABEL: Record<EntityType, string> = {
  track: "Tracks",
  album: "Albums",
  artist: "Artists",
  playlist: "Spotify playlists",
  folder: "Folders",
  local_playlist: "Local playlists",
  genre: "Genres",
};

interface FieldDraft {
  label: string;
  type: FieldType;
  group_name: string;
  entity_types: EntityType[];
  placeholder: string;
  rows: string;
  min: string;
  max: string;
  precision: string;
  options: SelectOption[];
}

function emptyDraft(existingLabels: readonly string[]): FieldDraft {
  return {
    label: nextDefaultName("New field", existingLabels),
    type: "text",
    group_name: "",
    entity_types: ["track"],
    placeholder: "",
    rows: "4",
    min: "",
    max: "",
    precision: "",
    options: [],
  };
}

function draftFromDef(def: FieldDef): FieldDraft {
  const cfg = def.config ?? {};
  return {
    label: def.label,
    type: def.type,
    group_name: def.group_name ?? "",
    entity_types: [...def.entity_types],
    placeholder: cfg.placeholder ?? "",
    rows: cfg.rows !== undefined ? String(cfg.rows) : "4",
    min: cfg.min !== undefined ? String(cfg.min) : "",
    max: cfg.max !== undefined ? String(cfg.max) : "",
    precision: cfg.precision !== undefined ? String(cfg.precision) : "",
    options: cfg.options ? cfg.options.map((o) => ({ ...o })) : [],
  };
}

function buildConfig(draft: FieldDraft): FieldConfig | undefined {
  const config: FieldConfig = {};

  if (draft.placeholder.trim()) config.placeholder = draft.placeholder.trim();

  if (draft.type === "longtext") {
    const rows = Number(draft.rows);
    if (Number.isFinite(rows) && rows >= 1) config.rows = Math.min(40, Math.floor(rows));
  }

  if (draft.type === "number") {
    if (draft.min.trim() !== "") {
      const min = Number(draft.min);
      if (Number.isFinite(min)) config.min = min;
    }
    if (draft.max.trim() !== "") {
      const max = Number(draft.max);
      if (Number.isFinite(max)) config.max = max;
    }
    if (draft.precision.trim() !== "") {
      const precision = Number(draft.precision);
      if (Number.isFinite(precision) && precision >= 0) {
        config.precision = Math.min(6, Math.floor(precision));
      }
    }
  }

  if (draft.type === "select" || draft.type === "multiselect") {
    const options = draft.options
      .map((o) => ({
        id: o.id.trim() || slugify(o.label) || "option",
        label: o.label.trim(),
        ...(o.color ? { color: o.color } : {}),
      }))
      .filter((o) => o.label.length > 0);
    if (options.length > 0) config.options = options;
  }

  return Object.keys(config).length > 0 ? config : undefined;
}

function uniqueOptionId(label: string, existing: readonly string[]): string {
  const base = slugify(label) || "option";
  if (!existing.includes(base)) return base;
  let n = 2;
  while (existing.includes(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

type EditorMode =
  | { kind: "create"; draft: FieldDraft }
  | { kind: "edit"; id: string; draft: FieldDraft };

export function FieldsManager() {
  const qc = useQueryClient();
  const fields = useAllFieldDefs();
  const create = useCreateField();
  const update = useUpdateField();
  const archive = useArchiveField();
  const reorder = useReorderField();
  const remove = useDeleteField();

  const [editor, setEditor] = useState<EditorMode | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FieldDef | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const list = useMemo(() => {
    const all = fields.data ?? [];
    return showArchived ? all : all.filter((f) => !f.archived);
  }, [fields.data, showArchived]);

  const archivedCount = (fields.data ?? []).filter((f) => f.archived).length;

  function openCreate() {
    setEditor({
      kind: "create",
      draft: emptyDraft((fields.data ?? []).map((f) => f.label)),
    });
  }

  function openEdit(def: FieldDef) {
    setEditor({ kind: "edit", id: def.id, draft: draftFromDef(def) });
  }

  function setDraft(patch: Partial<FieldDraft>) {
    setEditor((current) => (current ? { ...current, draft: { ...current.draft, ...patch } } : null));
  }

  function toggleEntity(type: EntityType) {
    if (!editor) return;
    const current = editor.draft.entity_types;
    const next = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    if (next.length === 0) return;
    setDraft({ entity_types: next });
  }

  async function save() {
    if (!editor) return;
    const { draft } = editor;
    const label = draft.label.trim();
    if (!label) return;
    if (draft.entity_types.length === 0) return;

    const config = buildConfig(draft) ?? null;

    if (editor.kind === "create") {
      const created = await create.mutateAsync({
        label,
        type: draft.type,
        group_name: draft.group_name.trim() || null,
        entity_types: draft.entity_types,
        ...(config ? { config } : {}),
      });
      setEditor({ kind: "edit", id: created.id, draft: draftFromDef(created) });
      return;
    }

    await update.mutateAsync({
      id: editor.id,
      patch: {
        label,
        group_name: draft.group_name.trim() || null,
        entity_types: draft.entity_types,
        config,
      },
    });
  }

  function move(def: FieldDef, direction: -1 | 1) {
    const index = list.findIndex((f) => f.id === def.id);
    if (index < 0) return;
    const neighbour = list[index + direction];
    if (!neighbour) return;
    if (direction === -1) {
      reorder.mutate({ id: def.id, before_id: neighbour.id, after_id: null });
    } else {
      reorder.mutate({ id: def.id, after_id: neighbour.id, before_id: null });
    }
  }

  const [bulkCount, setBulkCount] = useState("3");
  const [bulkGroup, setBulkGroup] = useState("Texts");
  const [bulkEntity, setBulkEntity] = useState<EntityType>("track");
  const [bulkBusy, setBulkBusy] = useState(false);

  const busy =
    create.isPending ||
    update.isPending ||
    archive.isPending ||
    reorder.isPending ||
    remove.isPending ||
    bulkBusy;

  async function addManyLongtexts() {
    const n = Math.min(20, Math.max(1, Math.floor(Number(bulkCount)) || 1));
    const group = bulkGroup.trim() || "Texts";
    const existingLabels = (fields.data ?? []).map((f) => f.label);
    const existingKeys = new Set((fields.data ?? []).map((f) => f.key));

    setBulkBusy(true);
    try {
      for (let i = 0; i < n; i += 1) {
        const label = nextDefaultName("Text", existingLabels);
        existingLabels.push(label);
        let key = slugify(label) || `text-${i + 1}`;
        let suffix = 2;
        while (existingKeys.has(key)) {
          key = `${slugify(label) || "text"}-${suffix}`;
          suffix += 1;
        }
        existingKeys.add(key);
        await api("/api/fields", {
          method: "POST",
          json: {
            label,
            key,
            type: "longtext",
            group_name: group,
            entity_types: [bulkEntity],
            config: { rows: 2 },
          },
        });
      }
      toast.success(`Added ${n} long text${n === 1 ? "" : "s"}`);
      void qc.invalidateQueries({ queryKey: ["fields"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add fields");
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Custom fields</h2>
          <p className="mt-1 text-sm text-muted">
            Definitions drive the detail sheet and bulk edits. Type is locked after create —
            delete and remake if you need a different type. Prefer real fields over a “slot
            count” — each one stays filterable and renameable.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {archivedCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              aria-pressed={showArchived}
              onClick={() => setShowArchived((v) => !v)}
            >
              {showArchived ? "Hide archived" : `Show archived (${archivedCount})`}
            </Button>
          )}
          <Button variant="secondary" size="sm" disabled={busy} onClick={openCreate}>
            <Plus className="size-3.5" />
            Add field
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-line bg-panel p-3">
        <label className="block space-y-1">
          <span className="text-xs text-muted">Add N long texts</span>
          <input
            type="number"
            min={1}
            max={20}
            value={bulkCount}
            disabled={busy}
            className={cn(INPUT, "w-20")}
            onChange={(e) => setBulkCount(e.target.value)}
          />
        </label>
        <label className="block min-w-36 flex-1 space-y-1">
          <span className="text-xs text-muted">Group</span>
          <input
            value={bulkGroup}
            disabled={busy}
            className={INPUT}
            onChange={(e) => setBulkGroup(e.target.value)}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-muted">Applies to</span>
          <select
            value={bulkEntity}
            disabled={busy}
            className={INPUT}
            onChange={(e) => setBulkEntity(e.target.value as EntityType)}
          >
            {ENTITY_TYPES.map((type) => (
              <option key={type} value={type}>
                {ENTITY_LABEL[type]}
              </option>
            ))}
          </select>
        </label>
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => void addManyLongtexts()}
        >
          {bulkBusy ? "Adding…" : "Add"}
        </Button>
      </div>

      {fields.isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}

      {fields.isError && (
        <EmptyState
          title="Couldn't load fields"
          description={fields.error instanceof Error ? fields.error.message : "Unknown error"}
          action={
            <Button variant="secondary" size="sm" onClick={() => void fields.refetch()}>
              Retry
            </Button>
          }
        />
      )}

      {fields.isSuccess && list.length === 0 && !editor && (
        <EmptyState
          title="No custom fields yet"
          description="Add one — it shows up on matching entities in the detail sheet with no code change."
          action={
            <Button variant="secondary" onClick={openCreate}>
              Add field
            </Button>
          }
        />
      )}

      {fields.isSuccess && list.length > 0 && (
        <ul className="divide-y divide-line rounded-lg border border-line">
          {list.map((def, index) => {
            const selected = editor?.kind === "edit" && editor.id === def.id;
            return (
              <li
                key={def.id}
                className={cn(
                  "flex items-center gap-2 px-3 py-2",
                  selected && "bg-row-selected",
                  def.archived && "opacity-60",
                )}
              >
                <div className="flex shrink-0 flex-col">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-6"
                    aria-label={`Move ${def.label} up`}
                    disabled={busy || index === 0}
                    onClick={() => move(def, -1)}
                  >
                    <ChevronUp className="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-6"
                    aria-label={`Move ${def.label} down`}
                    disabled={busy || index === list.length - 1}
                    onClick={() => move(def, 1)}
                  >
                    <ChevronDown className="size-3.5" />
                  </Button>
                </div>

                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => openEdit(def)}
                >
                  <div className="truncate font-medium">
                    {def.label}
                    {def.archived && (
                      <span className="ml-2 text-xs font-normal text-faint">archived</span>
                    )}
                  </div>
                  <div className="truncate text-xs text-faint">
                    {TYPE_LABEL[def.type]}
                    {def.group_name ? ` · ${def.group_name}` : ""}
                    {" · "}
                    {def.entity_types.map((t) => ENTITY_LABEL[t]).join(", ")}
                    {" · "}
                    <span className="font-mono">{def.key}</span>
                  </div>
                </button>

                <div className="flex shrink-0 gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-7"
                    aria-label={`Edit ${def.label}`}
                    onClick={() => openEdit(def)}
                  >
                    <Pencil className="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-7"
                    aria-label={def.archived ? `Restore ${def.label}` : `Archive ${def.label}`}
                    disabled={busy}
                    onClick={() =>
                      archive.mutate({ id: def.id, archived: !def.archived })
                    }
                  >
                    {def.archived ? (
                      <ArchiveRestore className="size-3.5" />
                    ) : (
                      <Archive className="size-3.5" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-7 text-status-cancelled"
                    aria-label={`Delete ${def.label}`}
                    disabled={busy}
                    onClick={() => setPendingDelete(def)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editor && (
        <FieldEditorForm
          mode={editor.kind}
          draft={editor.draft}
          busy={busy}
          onChange={setDraft}
          onToggleEntity={toggleEntity}
          onSave={() => void save()}
          onCancel={() => setEditor(null)}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={pendingDelete ? `Delete “${pendingDelete.label}”?` : "Delete field?"}
        description="This permanently removes the field and every value stored on it. Archive instead if you only want it hidden."
        confirmLabel="Delete field"
        busy={remove.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return;
          const id = pendingDelete.id;
          setPendingDelete(null);
          if (editor?.kind === "edit" && editor.id === id) setEditor(null);
          remove.mutate(id);
        }}
      />
    </div>
  );
}

function FieldEditorForm({
  mode,
  draft,
  busy,
  onChange,
  onToggleEntity,
  onSave,
  onCancel,
}: {
  mode: "create" | "edit";
  draft: FieldDraft;
  busy: boolean;
  onChange: (patch: Partial<FieldDraft>) => void;
  onToggleEntity: (type: EntityType) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const needsOptions = draft.type === "select" || draft.type === "multiselect";

  function addOption() {
    const ids = draft.options.map((o) => o.id);
    const label = nextDefaultName(
      "Option",
      draft.options.map((o) => o.label),
    );
    onChange({
      options: [...draft.options, { id: uniqueOptionId(label, ids), label }],
    });
  }

  function patchOption(index: number, patch: Partial<SelectOption>) {
    onChange({
      options: draft.options.map((opt, i) => (i === index ? { ...opt, ...patch } : opt)),
    });
  }

  function removeOption(index: number) {
    onChange({ options: draft.options.filter((_, i) => i !== index) });
  }

  return (
    <section className="space-y-4 rounded-lg border border-line bg-panel-elevated p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          {mode === "create" ? "New field" : "Edit field"}
        </h3>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={busy || !draft.label.trim() || draft.entity_types.length === 0}
            onClick={onSave}
          >
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block space-y-1 sm:col-span-2">
          <span className="text-xs text-muted">Label</span>
          <input
            value={draft.label}
            disabled={busy}
            className={INPUT}
            onChange={(e) => onChange({ label: e.target.value })}
          />
        </label>

        <label className="block space-y-1">
          <span className="text-xs text-muted">Type</span>
          <select
            value={draft.type}
            disabled={busy || mode === "edit"}
            className={INPUT}
            title={mode === "edit" ? "Type cannot change after create" : undefined}
            onChange={(e) => onChange({ type: e.target.value as FieldType })}
          >
            {FIELD_TYPES.map((type) => (
              <option key={type} value={type}>
                {TYPE_LABEL[type]}
              </option>
            ))}
          </select>
          {mode === "edit" && (
            <span className="block text-xs text-faint">Locked — delete and recreate to change.</span>
          )}
          {draft.type === "country" && (
            <span className="block text-xs text-faint">
              Searchable ISO 3166-1 list (English). Stores country codes, plus Other.
            </span>
          )}
        </label>

        <label className="block space-y-1">
          <span className="text-xs text-muted">Group</span>
          <input
            value={draft.group_name}
            disabled={busy}
            placeholder="e.g. Production"
            className={INPUT}
            onChange={(e) => onChange({ group_name: e.target.value })}
          />
        </label>

        <label className="block space-y-1 sm:col-span-2">
          <span className="text-xs text-muted">Placeholder</span>
          <input
            value={draft.placeholder}
            disabled={busy}
            className={INPUT}
            onChange={(e) => onChange({ placeholder: e.target.value })}
          />
        </label>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-xs text-muted">Applies to</legend>
        <div className="flex flex-wrap gap-3">
          {ENTITY_TYPES.map((type) => {
            const checked = draft.entity_types.includes(type);
            return (
              <label key={type} className="flex items-center gap-2 text-sm text-muted">
                <Checkbox
                  checked={checked}
                  disabled={busy || (checked && draft.entity_types.length === 1)}
                  onCheckedChange={() => onToggleEntity(type)}
                />
                {ENTITY_LABEL[type]}
              </label>
            );
          })}
        </div>
      </fieldset>

      {draft.type === "longtext" && (
        <label className="block max-w-xs space-y-1">
          <span className="text-xs text-muted">Rows</span>
          <input
            type="number"
            min={1}
            max={40}
            value={draft.rows}
            disabled={busy}
            className={INPUT}
            onChange={(e) => onChange({ rows: e.target.value })}
          />
        </label>
      )}

      {draft.type === "number" && (
        <div className="grid max-w-xl gap-3 sm:grid-cols-3">
          <label className="block space-y-1">
            <span className="text-xs text-muted">Min</span>
            <input
              type="number"
              value={draft.min}
              disabled={busy}
              className={INPUT}
              onChange={(e) => onChange({ min: e.target.value })}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs text-muted">Max</span>
            <input
              type="number"
              value={draft.max}
              disabled={busy}
              className={INPUT}
              onChange={(e) => onChange({ max: e.target.value })}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs text-muted">Precision</span>
            <input
              type="number"
              min={0}
              max={6}
              value={draft.precision}
              disabled={busy}
              className={INPUT}
              onChange={(e) => onChange({ precision: e.target.value })}
            />
          </label>
        </div>
      )}

      {needsOptions && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted">Options</span>
            <Button variant="ghost" size="sm" disabled={busy} onClick={addOption}>
              <Plus className="size-3.5" />
              Add option
            </Button>
          </div>
          {draft.options.length === 0 ? (
            <p className="text-xs text-faint">Add at least one option for this field to be useful.</p>
          ) : (
            <ul className="space-y-2">
              {draft.options.map((opt, index) => (
                <li key={`${opt.id}-${index}`} className="flex items-center gap-2">
                  <input
                    value={opt.label}
                    disabled={busy}
                    aria-label={`Option ${index + 1} label`}
                    className={INPUT}
                    onChange={(e) => {
                      const label = e.target.value;
                      const id =
                        mode === "create" || !opt.id
                          ? uniqueOptionId(
                              label,
                              draft.options.filter((_, i) => i !== index).map((o) => o.id),
                            )
                          : opt.id;
                      patchOption(index, { label, id });
                    }}
                  />
                  <span className="hidden shrink-0 font-mono text-xs text-faint sm:inline">
                    {opt.id}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-7 shrink-0 text-status-cancelled"
                    aria-label={`Remove ${opt.label || "option"}`}
                    disabled={busy}
                    onClick={() => removeOption(index)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
